import React, { useEffect, useState } from "react";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import axiosWithAuth from "../../utils/axiosWithAuth";
import {
  buildOrderData,
  isRestaurantOrder,
} from "../../hooks/useCreateOrder";
import { Button, message, Modal } from "antd";
import getBackendErrorMessage from "../../utils/getBackendErrorMessage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import gsap from "gsap";
import { FE_URL } from "../../common/constants";
import PaymentProcessing from "./PaymentProcessing";

const ORDER_STATUS_POLL_INTERVAL_MS = 2000;
const ORDER_STATUS_TIMEOUT_MS = 60000;

const wait = (milliseconds) =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const pollOrderStatus = async (paymentIntentId, restaurantOrder) => {
  const statusURL = restaurantOrder
    ? `/api/restaurant/order/status/${encodeURIComponent(paymentIntentId)}`
    : `/api/orders/status/${encodeURIComponent(paymentIntentId)}`;
  const deadline = Date.now() + ORDER_STATUS_TIMEOUT_MS;

  while (Date.now() < deadline) {
    try {
      const response = await axiosWithAuth().get(statusURL);

      console.log("[Bizzll] Order status response:", response);

      if (response.status === 200) {
        return { result: "success", response };
      }

      return { result: "error" };
    } catch (error) {
      const status = error.response?.status;

      console.log("[Bizzll] Order status polling response:", {
        status,
        response: error.response,
      });

      if (status === 404) {
        const remainingTime = deadline - Date.now();

        if (remainingTime <= 0) {
          break;
        }

        await wait(Math.min(ORDER_STATUS_POLL_INTERVAL_MS, remainingTime));
        continue;
      }

      if (status === 403) {
        return { result: "forbidden" };
      }

      if (status === 500) {
        return { result: "server-error" };
      }

      return { result: "error" };
    }
  }

  return { result: "timeout" };
};

const showOrderStatusError = (result) => {
  const errorMessages = {
    forbidden: "We couldn't verify this order.",
    "server-error": "The server couldn't verify your order.",
    timeout:
      "Your order is still being processed. Please try checking again later.",
    error: "We couldn't verify the status of your order.",
  };

  Modal.error({
    title: "Order verification failed",
    content: errorMessages[result] || errorMessages.error,
  });
};

const getOrderId = (response) => {
  const responseData = response?.data;
  const orderIdKeys = ["orderID", "orderId", "order_id", "id_order", "id"];
  const containers = [
    responseData,
    responseData?.order,
    responseData?.data,
    responseData?.data?.order,
  ];

  for (const container of containers) {
    for (const key of orderIdKeys) {
      if (container?.[key] !== undefined && container?.[key] !== null) {
        return container[key];
      }
    }
  }

  return null;
};

export default function PaymentInformation(props) {
  const { user, currentStep, tosendpayload } = props;
  const stripe = useStripe();
  const elements = useElements();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWaitingForOrder, setIsWaitingForOrder] = useState(false);
  const [collapse, setCollapse] = useState(false);
  const [divClass, setDivClass] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();

    console.log("[Bizzll] Payment submit clicked", {
      hasStripe: Boolean(stripe),
      hasElements: Boolean(elements),
      tosendpayload,
    });

    if (!stripe || !elements) {
      console.warn("[Bizzll] Payment submit stopped: Stripe is not ready");
      return;
    }

    setIsSubmitting(true);

    try {
      const cardElement = elements.getElement(CardNumberElement);

      console.log("[Bizzll] Creating Stripe payment method");

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });

      if (error) {
        console.error("[Bizzll] Stripe createPaymentMethod error:", error);
        message.error(
          getBackendErrorMessage(error, "Error processing payment"),
        );
        return;
      }

      const orderData = buildOrderData({
        user,
        cartItems: tosendpayload.cartItems,
        userInfo: tosendpayload.userInfo,
        subTotal: tosendpayload.subTotal,
        amountTotal: tosendpayload.amount,
        taxBreakdown: tosendpayload.tax_breakdown,
        deliveryPrice: tosendpayload.deliveryPrice,
        tip: tosendpayload.tipAmount ?? 0,
        deliveryType: tosendpayload.deliveryType,
      });

      const paymentIntentPayload = {
        amount: tosendpayload.amount,
        cartItems: tosendpayload.cartItems,
        customerID: tosendpayload.customerID,
        subTotal: tosendpayload.subTotal,
        paymentMethodId: paymentMethod.id,
        orderData,
      };

      console.log(
        "[Bizzll] Stripe payment intent payload to send:",
        paymentIntentPayload,
      );

      const paymentIntentRes = await axiosWithAuth().post(
        "/api/stripe/checkout/paymentIntent",
        paymentIntentPayload,
      );

      console.log(
        "[Bizzll] Stripe payment intent full response:",
        paymentIntentRes,
      );

      const paymentIntent = paymentIntentRes.data;
      console.log("[Bizzll] Stripe payment intent response:", paymentIntent);

      if (paymentIntent?.error) {
        message.error(
          getBackendErrorMessage(
            paymentIntent,
            "Error creating payment intent",
          ),
        );
        return;
      }

      if (paymentIntent?.requiresNewPaymentMethod) {
        message.error(
          paymentIntent?.message || "Please use a different payment method",
        );
        return;
      }

      const clientSecret =
        typeof paymentIntent === "string"
          ? paymentIntent
          : paymentIntent?.clientSecret ||
            paymentIntent?.client_secret ||
            paymentIntent?.paymentIntent?.client_secret;

      if (!clientSecret) {
        console.error(
          "[Bizzll] Missing client secret in payment intent response",
          paymentIntent,
        );
        message.error("Missing client secret in payment intent response");
        return;
      }

      const {
        error: confirmError,
        paymentIntent: confirmedPaymentIntent,
      } = await stripe.confirmCardPayment(clientSecret);

      console.log("[Bizzll] Stripe payment confirmation response:", {
        error: confirmError,
        paymentIntent: confirmedPaymentIntent,
      });

      if (confirmError) {
        console.error(
          "[Bizzll] Stripe confirmCardPayment error:",
          confirmError,
        );
        message.error(
          getBackendErrorMessage(confirmError, "Error confirming payment"),
        );
        return;
      }

      const paymentIntentId =
        confirmedPaymentIntent?.id ||
        paymentIntent?.paymentIntentId ||
        paymentIntent?.payment_intent_id ||
        paymentIntent?.id ||
        paymentIntent?.paymentIntent?.id;

      if (!paymentIntentId) {
        console.error(
          "[Bizzll] Missing payment intent ID after confirmation",
          {
            paymentIntentResponse: paymentIntent,
            confirmedPaymentIntent,
          },
        );
        message.error("Missing payment intent ID after confirmation");
        return;
      }

      console.log("[Bizzll] Payment Intent ID:", paymentIntentId);

      const restaurantOrder = isRestaurantOrder(tosendpayload.cartItems);
      setIsWaitingForOrder(true);

      const orderStatus = await pollOrderStatus(
        paymentIntentId,
        restaurantOrder,
      );

      if (orderStatus.result === "success") {
        const orderId = getOrderId(orderStatus.response);

        if (!orderId) {
          console.error(
            "[Bizzll] Missing order ID in successful status response",
            orderStatus.response,
          );
          showOrderStatusError("error");
          return;
        }

        const confirmationPath = restaurantOrder
          ? `/order-confirmation-restaurant/${orderId}`
          : `/order-confirmation/${orderId}`;

        window.location.replace(`${FE_URL}${confirmationPath}`);
        return;
      }

      showOrderStatusError(orderStatus.result);
    } catch (error) {
      console.error("[Bizzll] Payment submit failed:", error);
      message.error(
        getBackendErrorMessage(
          error,
          "Error processing order. Please try again",
        ),
      );
    } finally {
      setIsWaitingForOrder(false);
      setIsSubmitting(false);
    }
  };

  const CARD_NUMBER_OPTIONS = {
    showIcon: true,
    style: {
      base: {
        color: "#32325d",
        fontFamily: '"Nunito Sans", Helvetica, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a",
      },
    },
  };

  const CARD_EXPIRY_OPTIONS = {
    style: {
      base: {
        color: "#32325d",
        fontFamily: '"Nunito Sans", Helvetica, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a",
      },
    },
  };

  const CARD_CVC_OPTIONS = {
    style: {
      base: {
        color: "#32325d",
        fontFamily: '"Nunito Sans", Helvetica, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a",
      },
    },
  };

  useEffect(() => {
    let classNames;

    if (currentStep > 3) {
      classNames = "";
      setCollapse(true);

      gsap.to("#payment-information .step-content", {
        height: 0,
        padding: 0,
        duration: 0.3,
      });
    } else if (currentStep < 3) {
      classNames = "";
      setCollapse(true);

      gsap.to("#payment-information .step-content", {
        height: 0,
        padding: 0,
        duration: 0.3,
      });
    } else {
      classNames = "is-active incomplete";
      setCollapse(false);

      gsap.to("#payment-information .step-content", {
        height: "auto",
        padding: 10,
        duration: 0.3,
      });
    }

    setDivClass(classNames);
  }, [currentStep]);

  return (
    <>
      <PaymentProcessing visible={isWaitingForOrder} />

      <div
        id="payment-information"
        className={divClass ? `step-wrapper ${divClass}` : "step-wrapper"}
      >
        <div className="step-heading">
          <div className="icon">
            {collapse && currentStep > 2 ? (
              <FontAwesomeIcon icon={faCheck} />
            ) : (
              "3"
            )}
          </div>

          <div className="title">Payment Method</div>
        </div>

        <div className="step-content">
          <form onSubmit={handleSubmit}>
            <div className="fields">
              <div className="card-element card-element-full">
                <CardNumberElement options={CARD_NUMBER_OPTIONS} />
              </div>
              <div className="card-element card-element-half">
                <CardExpiryElement options={CARD_EXPIRY_OPTIONS} />
              </div>
              <div className="card-element card-element-half">
                <CardCvcElement options={CARD_CVC_OPTIONS} />
              </div>
            </div>

            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              disabled={!stripe}
            >
              Process Order
            </Button>
          </form>
        </div>
      </div>
    </>
  );
}
